import { z } from 'zod';

/**
 * Build a Zod schema for direct edit validation based on column validation rules
 * @param {ApiManagerTableValidation} validation - Validation rules from column
 * @returns {import('zod').ZodTypeAny} Zod schema for TableDirectEditValue validation
 */
export const buildDirectEditSchema = (validation) => {
  if (!validation) {
    return z.string().optional();
  }

  let schema = z.string();

  if (validation.required === 'oui') {
    schema = schema.min(1, 'directEdit.validation.required');
  }

  if (validation.maxLength !== null && validation.maxLength !== undefined) {
    schema = schema.max(validation.maxLength, 'directEdit.validation.maxLength');
  }

  if (validation.regex !== null && validation.regex !== undefined) {
    try {
      const regex = new RegExp(validation.regex);
      schema = schema.regex(regex, 'directEdit.validation.regex');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Invalid regex pattern in validation:', validation.regex);
    }
  }

  if (validation.required !== 'oui') {
    // @ts-ignore - Union type is valid for optional fields
    schema = z.union([schema, z.literal('')]);
  }

  return schema;
};

/**
 * Validate a direct edit value against column validation rules using Zod
 * @param {TableDirectEditValue} value - The value to validate
 * @param {ApiManagerTableValidation} validation - Validation rules from column
 * @returns {{ isValid: boolean, error: string | null }} Validation result
 */
export const validateDirectEditValue = (value, validation) => {
  if (!validation) {
    return { isValid: true, error: null };
  }

  const schema = buildDirectEditSchema(validation);
  const result = schema.safeParse(value);

  if (result.success) {
    return { isValid: true, error: null };
  }

  const firstError = result.error.issues[0];
  const errorMessage = firstError?.message || 'directEdit.validation.failed';

  return {
    isValid: false,
    error: errorMessage,
  };
};

/**
 * Get the active direct edit type from directEdit config
 * @param {ApiManagerTableDirectEditObject | null} directEdit - Direct edit configuration
 * @returns {string | null} The active direct edit type or null
 */
export const getDirectEditType = (directEdit) => {
  if (!directEdit) return null;

  // @ts-ignore - directEdit is a dynamic object with optional properties
  return Object.keys(directEdit).find((key) => directEdit[key] !== null) || null;
};

/**
 * Direct edit types that open edit mode on double-click only (text-like / date / time).
 * Other types (e.g. color, selectClass) activate on single click.
 * @type {string[]}
 */
export const DIRECT_EDIT_TYPES_WITH_DOUBLE_CLICK = [
  'text',
  'textarea',
  'time',
  'date',
  'datetime',
];

/**
 * Whether a direct-edit type activates on double-click rather than single click.
 * @param {string | null | undefined} type - Direct edit type
 * @returns {boolean}
 */
export const isDirectEditDoubleClickType = (type) => {
  if (!type) return false;
  return DIRECT_EDIT_TYPES_WITH_DOUBLE_CLICK.includes(type);
};

/**
 * Get the active value from the cell value object
 * @param {TableCellValueObjectCustom} cellValue - Cell value object
 * @returns {TableDirectEditValue} The active value
 */
export const getCellActiveValue = (cellValue) => {
  if (!cellValue?.value) return null;
  const activeValue = Object.values(cellValue.value).find((value) => value !== null);
  return activeValue || null;
};

/**
 * Date direct-edit raw string: always prefer `value.string` (display/API date text).
 * @param {TableCellValueObjectCustom | null | undefined} cellValue
 * @returns {string}
 */
export const getCellDateStringValue = (cellValue) => {
  const stringField = cellValue?.value?.string;
  if (stringField !== null && stringField !== undefined && stringField !== '') {
    return typeof stringField === 'string' ? stringField : String(stringField);
  }
  const activeValue = getCellActiveValue(cellValue);
  if (activeValue === null || activeValue === undefined || activeValue === '') {
    return '';
  }
  return typeof activeValue === 'string' ? activeValue : String(activeValue);
};

/**
 * Whether the cell has no display data (null or empty string).
 * Action hint icons (link / edit) are shown only when this is true.
 * @param {TableCellValueObjectCustom} cellValue - Cell value object
 * @returns {boolean}
 */
export const isCellDisplayValueEmpty = (cellValue) => {
  const activeValue = getCellActiveValue(cellValue);
  if (activeValue === null || activeValue === undefined) return true;
  if (typeof activeValue === 'string') return activeValue === '';
  if (Array.isArray(activeValue)) return activeValue.length === 0;
  return false;
};

/**
 * Whether an empty cell should hide its read-only placeholder (basicUrl or direct edit available).
 * @param {TableCellValueObjectCustom | null | undefined} cellValue - Cell value object
 * @returns {boolean}
 */
export const shouldHideEmptyCellPlaceholder = (cellValue) => {
  if (cellValue?.basicUrl != null) return true;
  return getDirectEditType(cellValue?.directEdit) !== null;
};

/**
 * Placeholder to show when a cell value is empty; cleared when the cell is actionable.
 * @param {TableCellValueObjectCustom | null | undefined} cellValue - Cell value object
 * @param {string} defaultPlaceholder - Default placeholder when the cell is not actionable
 * @returns {string}
 */
export const getCellEmptyPlaceholder = (cellValue, defaultPlaceholder) => (
  shouldHideEmptyCellPlaceholder(cellValue) ? '' : defaultPlaceholder
);

/**
 * Normalize a value for comparison (extract string value from boolean object if needed)
 * @param {TableDirectEditValue} value - Value to normalize
 * @returns {string | boolean | null} Normalized string value or boolean value or null
 */
const normalizeValueForComparison = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') return value;
  // Handle boolean object: ApiManagerTableCellBooleanObject
  if (typeof value === 'object' && value !== null && 'value' in value) {
    return /** @type {ApiManagerTableCellBooleanObject} */ (value).value || null;
  }
  // Handle arrays (lists) - convert to string representation for comparison
  if (Array.isArray(value)) {
    return JSON.stringify(value);
  }
  return String(value);
};

/**
 * Check if the value has changed and should be saved
 * @param {TableDirectEditValue} editValue - The edited value
 * @param {TableDirectEditValue} cellActiveValue - The original cell value
 * @returns {boolean} True if value has changed
 */
export const shouldSaveValue = (
  editValue,
  cellActiveValue,
) => {
  const normalizedEdit = normalizeValueForComparison(editValue);
  const normalizedCell = normalizeValueForComparison(cellActiveValue);
  return normalizedEdit !== normalizedCell;
};

/**
 * Get the setAttributeUrl for a specific direct edit type
 * @param {ApiManagerTableDirectEditObject | null} directEdit - Direct edit configuration
 * @param {string} directEditType - Direct edit type
 * @returns {string | null} The setAttributeUrl or null
 */
export const getSetAttributeUrl = (directEdit, directEditType) => {
  if (!directEdit || !directEditType) return null;

  // @ts-ignore - directEdit is a dynamic object with optional properties
  const directEditConfig = directEdit[directEditType];
  return directEditConfig?.setAttributeUrl || null;
};

/**
 * Convert a boolean value to "oui" or "non" string for API submission
 * @param {boolean | null} value - Boolean value to convert
 * @returns {"oui" | "non" | null} String representation of the boolean value
 */
export const convertBooleanToOuiNon = (value) => {
  if (value === true) return 'oui';
  if (value === false) return 'non';
  return null;
};

/** Default icon for selectClass option color (matches SelectField color indicator). */
const SELECT_CLASS_COLOR_DOT_ICON = 'fas fa-square';

/**
 * Whether the list item uses option.color as badge fill (backgroundColor) rather than an icon dot.
 * @param {ApiManagerTableCellArrayItemObject | null | undefined} template - Existing list item
 * @returns {boolean}
 */
const listItemUsesBadgeBackgroundForColor = (template) => Boolean(
  template?.backgroundColor && !template?.icon && !template?.iconColor,
);

/**
 * List item icon for optimistic selectClass: keep non-color icons (e.g. checkmark), add/clear the color dot.
 * @param {ApiManagerTableCellArrayItemObject | null | undefined} template
 * @param {string | null} color
 * @returns {string | null}
 */
const resolveListItemIconForColorOption = (template, color) => {
  const templateIcon = template?.icon ?? null;
  if (color) {
    return templateIcon ?? SELECT_CLASS_COLOR_DOT_ICON;
  }
  if (templateIcon === SELECT_CLASS_COLOR_DOT_ICON) {
    return null;
  }
  return templateIcon;
};

/**
 * Build one list item for selectClass optimistic display, preserving the cell's visual convention.
 * API option `color` maps to iconColor + square icon (dot next to label), not cell background.
 * @param {ApiManagerTableCellArrayItemObject | null | undefined} template - Existing item to mirror (icon, path, etc.)
 * @param {string} label - Option lib
 * @param {string | null} color - Option color from selectOptions API
 * @returns {ApiManagerTableCellArrayItemObject}
 */
const buildListItemForSelectClassOption = (template, label, color) => {
  if (listItemUsesBadgeBackgroundForColor(template)) {
    return {
      lib: label,
      icon: template?.icon ?? null,
      iconColor: template?.iconColor ?? null,
      path: template?.path ?? null,
      optGroup: template?.optGroup ?? null,
      backgroundColor: color,
    };
  }

  return {
    lib: label,
    icon: resolveListItemIconForColorOption(template, color),
    iconColor: color,
    path: template?.path ?? null,
    optGroup: template?.optGroup ?? null,
    backgroundColor: template?.backgroundColor ?? null,
  };
};

/**
 * Build an optimistic cell patch for a selectClass save, matching the cell's existing visual shape.
 *
 * - List columns: patch `value.list` (keep `value.string` null). Option color → icon dot (iconColor)
 *   by default, OR list item backgroundColor when the existing item already used badge fill.
 * - String/enum columns: patch `value.string`. If the cell already uses `cell.backgroundColor`
 *   to express the class color, patch it with the picked option color (null if no color).
 * @param {TableCellValueObjectCustom | null | undefined} cellValue - The current cell (used to detect the shape)
 * @param {{ label: string | null, color: string | null }} pickedOption - Picked option (`id` is sent as API value separately)
 * @returns {import('../../services/directEdit/directEditCachePatch').DirectEditCellPatch}
 */
export const buildSelectClassOptimisticPatch = (cellValue, pickedOption) => {
  const label = pickedOption?.label ?? '';
  const color = pickedOption?.color ?? null;
  const isListCell = Array.isArray(cellValue?.value?.list);

  if (isListCell) {
    const template = cellValue?.value?.list?.[0];
    return {
      value: {
        string: null,
        list: label
          ? [buildListItemForSelectClassOption(template, label, color)]
          : [],
      },
    };
  }

  // If the cell currently expresses its class color via cell.backgroundColor, update it to the
  // picked option color so the visual stays consistent with the new label (mirror of the list
  // "badge background" branch). Cells that did not use backgroundColor are left untouched.
  const cellUsesBackgroundForColor = Boolean(cellValue?.backgroundColor);

  return {
    value: { string: label },
    ...(cellUsesBackgroundForColor ? { backgroundColor: color } : {}),
  };
};

/**
 * Transform actionChoice actions to a flat array format
 * @param {Array<ApiManagerTableDirectEditActionObject>} actions - Array of action objects
 * @returns {TableDirectEditFlatAction[]} Transformed actions
 */
export const transformDirectEditActions = (actions) => {
  if (!actions || actions.length === 0) return [];

  /** @type {TableDirectEditFlatAction[]} */
  const transformed = actions.flatMap((actionObj, index) => {
    /** @type {TableDirectEditFlatAction[]} */
    const actionEntries = [];

    if (actionObj?.openTab) {
      actionEntries.push({
        ...actionObj.openTab,
        id: `openTab-${index}-${actionObj.openTab.lib}`,
        type: 'openTab',
      });
    }

    if (actionObj?.openFormModal) {
      actionEntries.push({
        ...actionObj.openFormModal,
        id: `openFormModal-${index}-${actionObj.openFormModal.lib}`,
        type: 'openFormModal',
      });
    }

    if (actionObj?.clearCellValue) {
      actionEntries.push({
        ...actionObj.clearCellValue,
        id: `clearCellValue-${index}-${actionObj.clearCellValue.lib}`,
        type: 'clearCellValue',
      });
    }

    if (actionObj?.replaceCellValue) {
      actionEntries.push({
        ...actionObj.replaceCellValue,
        id: `replaceCellValue-${index}-${actionObj.replaceCellValue.lib}`,
        type: 'replaceCellValue',
      });
    }

    return actionEntries;
  });

  return transformed;
};
